import { useEffect, useState } from 'react';
import { Check, Plus } from 'lucide-react';
import {
  getTrialSlots,
  type ScheduleDay,
  type ScheduleSlot,
  type TrialClassChoice,
  type TrialDayChoice,
} from '@/lib/api';
import { FALLBACK_TRIAL_SLOTS } from './fallbackTrialSlots';

const DAY_LABEL: Record<ScheduleDay, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
};

/** Schedule weekday → JS `Date.getDay()` index (Sun = 0). */
const DAY_INDEX: Record<ScheduleDay, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

/** Local `YYYY-MM-DD` (no UTC shift — the gym books in its own timezone). */
function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** The next `count` calendar dates that fall on the given weekday (today included). */
function upcomingDatesForDay(day: ScheduleDay, count = 6): Date[] {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + ((DAY_INDEX[day] - d.getDay() + 7) % 7));
  const dates: Date[] = [];
  for (let i = 0; i < count; i += 1) {
    dates.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return dates;
}

function formatDateOption(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Soonest upcoming date matching a slot's weekday, as `YYYY-MM-DD`. */
function defaultDateForDay(day: ScheduleDay): string {
  return toISODate(upcomingDatesForDay(day, 1)[0]);
}

const CLASS_INFO: Record<
  TrialClassChoice,
  { label: string; image: string; objectPosition?: string }
> = {
  BOXING: {
    label: 'Boxing',
    image: '/assets/instagram/B1Fl9aAHr0S_01.jpg',
    objectPosition: '71% 36%',
  },
  MUAY_THAI: {
    label: 'Muay Thai',
    image: '/assets/instagram/Ch9qaeSOSXx_01.jpg',
    objectPosition: '50% 13%',
  },
  MMA: {
    label: 'MMA',
    image: '/assets/instagram/B4ihm1CHy2d_01.jpg',
    objectPosition: '50% 35%',
  },
  BJJ: {
    label: 'Brazilian Jiu-Jitsu',
    image: '/assets/instagram/CNF_KsWsPEM_01.jpg',
    objectPosition: '31% 78%',
  },
  KIDS_BOXING: {
    label: 'Kids Boxing',
    image: '/assets/programs/kids.jpg',
  },
  KIDS_BJJ: {
    label: 'Kids Jiu Jitsu',
    image: '/assets/programs/kids.jpg',
  },
  KIDS_WRESTLING: {
    label: 'Kids Wrestling',
    image: '/assets/programs/kids.jpg',
  },
};

/** Map a schedule slot to the back-end trial enums. */
function slotToTrialEnum(slot: ScheduleSlot): {
  classChoice: TrialClassChoice;
  dayChoice: TrialDayChoice;
} {
  const classChoice: TrialClassChoice =
    slot.category === 'boxing'
      ? 'BOXING'
      : slot.category === 'muay-thai'
        ? 'MUAY_THAI'
        : slot.category === 'mma'
          ? 'MMA'
          : slot.category === 'bjj'
            ? 'BJJ'
            : slot.category === 'kids'
              ? slot.title.toLowerCase().includes('jiu jitsu')
                ? 'KIDS_BJJ'
                : slot.title.toLowerCase().includes('wrestling')
                  ? 'KIDS_WRESTLING'
                  : 'KIDS_BOXING'
              : 'BOXING'; // mixed/wrestling default to BOXING for storage
  return { classChoice, dayChoice: slot.day };
}

function trialSlotTitle(slot: ScheduleSlot): string {
  return slot.notes ? `${slot.title} (${slot.notes})` : slot.title;
}

export interface TrialSlotPick {
  slotId: string;
  slotTitle: string;
  startTime: string;
  endTime: string;
  classChoice: TrialClassChoice;
  dayChoice: TrialDayChoice;
  /** Specific calendar date the member will attend, `YYYY-MM-DD`. */
  classDate: string;
  isKidsClass: boolean;
}

export function TrialDetailsStep(props: {
  pickedSlot: TrialSlotPick | null;
  setPickedSlot: (s: TrialSlotPick | null) => void;
  under18: boolean;
  setUnder18: (under18: boolean) => void;
}) {
  const [slots, setSlots] = useState<ScheduleSlot[]>(FALLBACK_TRIAL_SLOTS);

  useEffect(() => {
    getTrialSlots()
      .then((r) => {
        if (Array.isArray(r.slots) && r.slots.length > 0) setSlots(r.slots);
      })
      .catch(() => undefined);
  }, []);

  const availableClassChoices = Array.from(
    new Set(slots.map((slot) => slotToTrialEnum(slot).classChoice))
  );

  function onPick(id: string) {
    if (!id) return props.setPickedSlot(null);
    const slot = slots.find((s) => s.id === id);
    if (!slot) return;
    const { classChoice, dayChoice } = slotToTrialEnum(slot);
    // Keep the existing date if it still lands on the new slot's weekday,
    // otherwise default to the soonest matching date.
    const keepDate =
      props.pickedSlot?.dayChoice === dayChoice
        ? props.pickedSlot.classDate
        : defaultDateForDay(dayChoice);
    props.setPickedSlot({
      slotId: slot.id,
      slotTitle: trialSlotTitle(slot),
      startTime: slot.startTime,
      endTime: slot.endTime,
      classChoice,
      dayChoice,
      classDate: keepDate,
      isKidsClass: slot.category === 'kids',
    });
  }

  function onPickDate(classDate: string) {
    if (!props.pickedSlot) return;
    props.setPickedSlot({ ...props.pickedSlot, classDate });
  }

  function onPickClass(choice: TrialClassChoice) {
    const slot = slots.find((s) => slotToTrialEnum(s).classChoice === choice);
    if (slot) onPick(slot.id);
  }

  const isKidsClass = props.pickedSlot?.isKidsClass ?? false;
  const pickedClass = props.pickedSlot?.classChoice ?? null;

  return (
    <div className="grid gap-4">
      <fieldset>
        <legend className="mb-2 text-xs uppercase tracking-wider text-bone/60">Class</legend>
        <div className="grid gap-3">
          {availableClassChoices.map((choice) => {
            const info = CLASS_INFO[choice];
            const checked = pickedClass === choice;

            return (
              <button
                key={choice}
                type="button"
                onClick={() => onPickClass(choice)}
                className={`group grid grid-cols-[5.5rem_1fr_auto] items-center overflow-hidden rounded-lg border bg-neutral-900 text-left transition-colors sm:grid-cols-[6rem_1fr_auto] ${
                  checked
                    ? 'border-accent bg-accent/10'
                    : 'border-bone/15 bg-neutral-900 hover:border-bone/30'
                }`}
                aria-pressed={checked}
              >
                <div className="aspect-square overflow-hidden bg-neutral-800">
                  <img
                    src={info.image}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{ objectPosition: info.objectPosition }}
                  />
                </div>
                <span className="min-w-0 px-4 font-display text-xl leading-tight">
                  {info.label}
                </span>
                <span
                  aria-hidden
                  className={`mr-4 grid h-8 w-8 place-items-center rounded-full border ${
                    checked ? 'border-accent bg-accent text-ink' : 'border-bone/30 text-bone/60'
                  }`}
                >
                  {checked ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <label className="block">
        <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
          Pick a class day &amp; time
        </span>
        <select
          required
          value={props.pickedSlot?.slotId ?? ''}
          onChange={(e) => onPick(e.target.value)}
          className="input"
          aria-label="Class slot"
        >
          <option value="">Select a slot...</option>
          {slots.map((s) => (
            <option key={s.id} value={s.id}>
              {DAY_LABEL[s.day]} · {s.startTime} - {s.endTime} · {trialSlotTitle(s)}
            </option>
          ))}
        </select>
      </label>

      {props.pickedSlot && (
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
            Pick your {DAY_LABEL[props.pickedSlot.dayChoice]} date
          </span>
          <select
            required
            value={props.pickedSlot.classDate}
            onChange={(e) => onPickDate(e.target.value)}
            className="input"
            aria-label="Class date"
          >
            {upcomingDatesForDay(props.pickedSlot.dayChoice).map((d) => {
              const iso = toISODate(d);
              return (
                <option key={iso} value={iso}>
                  {formatDateOption(d)}
                </option>
              );
            })}
          </select>
        </label>
      )}

      <label className="flex items-center gap-3 text-sm">
        <input
          type="checkbox"
          checked={isKidsClass || props.under18}
          disabled={isKidsClass}
          onChange={(e) => props.setUnder18(e.target.checked)}
          className="h-4 w-4 rounded border-bone/30 bg-neutral-900 text-accent focus:ring-accent disabled:opacity-60"
        />
        Trial is for someone under 18
      </label>
    </div>
  );
}

export function summarySlot(s: TrialSlotPick): string {
  const [y, m, d] = s.classDate.split('-').map(Number);
  const dateLabel = Number.isFinite(y)
    ? new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : DAY_LABEL[s.dayChoice];
  return `${dateLabel} · ${s.startTime} - ${s.endTime} · ${s.slotTitle}`;
}
